import Select from '@components/Select';
import type { BaseComponentProps } from '@interfaces/selection';
import { useAudioOutputs } from '@providers/DevicesProvider';
import { useLogger } from '@providers/LoggerProvider';
import { useMeetingManager } from '@providers/MeetingProvider';
import type { ChangeEvent, FC, PropsWithChildren } from 'react';
import { Container } from './style';

interface SpeakerSelectionProps extends BaseComponentProps {
  /** The message that will be shown when no audio output speaker devices are found. */
  notFoundMsg?: string;
  /** The label that will be shown for speaker selection, it defaults to `Speaker source`. */
  label?: string;
  /** The callback fired when the selection is changed.
   *  It is required if you want to add testing functionality around speaker selection. */
  onChange?: (selectedAudioOutputDevice: string) => void;
}

const SpeakerSelection: FC<PropsWithChildren<SpeakerSelectionProps>> = ({
  notFoundMsg = 'No speaker devices found',
  label = 'Speaker source',
  onChange,
  ...rest
}) => {
  const logger = useLogger();
  const { devices } = useAudioOutputs();
  const meetingManager = useMeetingManager();

  const handleSelect = async (
    e: ChangeEvent<HTMLSelectElement>,
  ): Promise<void> => {
    try {
      const deviceId = e.target.value;
      await meetingManager.startAudioOutputDevice(deviceId);
      onChange && onChange(deviceId);
    } catch (error) {
      logger.error('SpeakerSelection failed to select speaker');
    }
  };

  return (
    <Container>
      <Select onChange={handleSelect}>
        {devices.map((device) => (
          <option value={device.deviceId} key={device.deviceId}>
            {device.label}
          </option>
        ))}
      </Select>
    </Container>
  );
};

export default SpeakerSelection;
